import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  adminListModuleFeedback, adminDecideModuleFeedback,
  adminListCommunityFeedback, adminDecideCommunityFeedback,
} from "@/lib/apiClient";
import { Star, Check, X, Clock } from "lucide-react";

const STATUS_FILTERS = ["pending", "approved", "rejected", "all"];

export default function AdminFeedback() {
  const [tab, setTab] = useState("module"); // "module" | "community"

  return (
    <div data-testid="admin-feedback-page">
      <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563]">Admin · Feedback</div>
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#1F2937] mt-1">Feedback moderation</h1>
      <p className="text-sm text-[#4B5563] mt-1">Review and approve or reject feedback before it's used elsewhere in the app.</p>

      <div className="mt-6 inline-flex items-center rounded-full border border-[#E7E0D2] bg-white overflow-hidden" data-testid="admin-feedback-tabs">
        <button data-testid="admin-feedback-tab-module" onClick={() => setTab("module")}
          className={`px-4 py-2 text-sm font-medium transition ${tab === "module" ? "bg-[#064E3B] text-white" : "text-[#4B5563] hover:text-[#1F2937]"}`}>
          Module Feedback
        </button>
        <button data-testid="admin-feedback-tab-community" onClick={() => setTab("community")}
          className={`px-4 py-2 text-sm font-medium transition ${tab === "community" ? "bg-[#064E3B] text-white" : "text-[#4B5563] hover:text-[#1F2937]"}`}>
          Community Feedback
        </button>
      </div>

      <div className="mt-6">
        {tab === "module"
          ? <FeedbackList list={adminListModuleFeedback} decide={adminDecideModuleFeedback} testId="module" showModuleCode />
          : <FeedbackList list={adminListCommunityFeedback} decide={adminDecideCommunityFeedback} testId="community" />}
      </div>
    </div>
  );
}

function FeedbackList({ list, decide, testId, showModuleCode }) {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    list(status === "all" ? null : status).then((r) => setItems(r.items || [])).catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (id, next) => {
    setBusyId(id);
    try {
      await decide(id, next);
      setItems((prev) => prev.filter((f) => (f.feedback_id) !== id));
      toast.success(next === "approved" ? "Approved" : "Rejected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update feedback");
    } finally { setBusyId(null); }
  };

  return (
    <div data-testid={`admin-feedback-list-${testId}`}>
      <div className="inline-flex items-center rounded-full border border-[#E7E0D2] bg-white overflow-hidden mb-4">
        {STATUS_FILTERS.map((s) => (
          <button key={s} data-testid={`admin-feedback-filter-${testId}-${s}`} onClick={() => setStatus(s)}
            className={`px-3 py-1.5 text-xs font-medium capitalize transition ${status === s ? "bg-[#064E3B] text-white" : "text-[#4B5563] hover:text-[#1F2937]"}`}>
            {s}
          </button>
        ))}
      </div>

      {items === null ? (
        <div className="h-24 bg-[#E7E0D2] rounded-2xl animate-pulse" />
      ) : items.length === 0 ? (
        <div className="text-center text-sm text-[#4B5563] py-10">No {status !== "all" ? status : ""} feedback.</div>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((f) => (
            <div key={f.feedback_id} className="rounded-2xl bg-white border border-[#E7E0D2] p-4" data-testid={`admin-feedback-item-${f.feedback_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-3.5 h-3.5 ${n <= f.rating ? "text-[#D97706] fill-[#D97706]" : "text-[#E7E0D2]"}`} />
                    ))}
                  </div>
                  <div className="text-xs text-[#4B5563] mt-1">
                    {f.user_name || "Anonymous"}
                    {showModuleCode && f.module_code && <span> · {f.module_code}</span>}
                    {" · "}{new Date(f.created_at).toLocaleDateString()}
                  </div>
                  {f.comment && <p className="mt-2 text-sm text-[#1F2937] italic">"{f.comment}"</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                    f.status === "approved" ? "bg-[#ECFDF5] text-[#064E3B] border border-[#A7F3D0]"
                    : f.status === "rejected" ? "bg-[#FEF2F2] text-[#EF4444] border border-[#FECACA]"
                    : "bg-[#FFF7ED] text-[#D97706] border border-[#FED7AA]"
                  }`}>
                    <Clock className="w-3 h-3" /> {f.status}
                  </span>
                  {f.status !== "approved" && (
                    <button data-testid={`admin-feedback-approve-${f.feedback_id}`} disabled={busyId === f.feedback_id}
                      onClick={() => act(f.feedback_id, "approved")}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-xs font-medium transition disabled:opacity-50">
                      <Check className="w-3.5 h-3.5" /> Approve
                    </button>
                  )}
                  {f.status !== "rejected" && (
                    <button data-testid={`admin-feedback-reject-${f.feedback_id}`} disabled={busyId === f.feedback_id}
                      onClick={() => act(f.feedback_id, "rejected")}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#E7E0D2] hover:border-[#EF4444] text-[#4B5563] hover:text-[#EF4444] text-xs font-medium transition disabled:opacity-50">
                      <X className="w-3.5 h-3.5" /> Reject
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
